import { useEffect, useState } from "react";

import { type AWSNodeConfigApiParameters, type CloudFlowNodeType, type OperationPointer } from "@doitintl/cmp-models";

import { cloudflowTexts } from "../../../../../../assets/texts";
import { useAWSAccounts } from "../../../../../Settings/AWS/hooks";
import { useGetServiceDescriptor } from "../../../Common/hooks/useGetServiceDescriptor";
import { useOperationData } from "../../../Common/hooks/useOperationData";
import { getAWSPermissionToCheck } from "../../../utils/awsUtils";
import { useAWSPermissionsCheck } from "../../hooks/useAWSPermissionsCheck";
import { useNodeConfigurationContext } from "../../NodeConfigurationContext";
import { useErrorUpdates } from "../hooks";
import { type WaiterAutocompleteValue } from "./useAWSWaiters";

type WaiterPermissionsListenerProps = {
  parameters: AWSNodeConfigApiParameters;
  selectedWaiter: WaiterAutocompleteValue;
};

export const WaiterPermissionsListener = ({ parameters, selectedWaiter }: WaiterPermissionsListenerProps) => {
  const { nodeConfig } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const waiterOperationPointer: OperationPointer = {
    id: selectedWaiter.operationModel.id,
    service: parameters.operation.service,
    version: parameters.operation.version,
    provider: parameters.operation.provider,
  };
  const { operationData } = useOperationData(waiterOperationPointer);
  const { serviceData } = useGetServiceDescriptor(waiterOperationPointer);
  const accountId = parameters.configurationValues.accountId;

  const checkPermissions = useAWSPermissionsCheck(nodeConfig.flowId);
  const [accounts] = useAWSAccounts();
  const [isWaiterHasPermissions, setIsWaiterHasPermissions] = useState(true);

  useErrorUpdates<CloudFlowNodeType.ACTION>({
    isValid: isWaiterHasPermissions,
    errorKey: "waiter_permission_error",
    errorMessage: cloudflowTexts.STATUS.WAITER_MISSING_PERMISSIONS_ERROR,
  });

  useEffect(() => {
    async function check() {
      if (!serviceData?.permissionsNamespace) {
        return;
      }

      const permissionToCheck = getAWSPermissionToCheck(
        waiterOperationPointer.id,
        serviceData.permissionsNamespace,
        operationData?.permissions
      );
      const account = accounts.find((account) => account.accountId === accountId);

      if (account) {
        const result = await checkPermissions(account, permissionToCheck);
        setIsWaiterHasPermissions(result.permissionsExists);
      }
    }
    check();
  }, [
    checkPermissions,
    accountId,
    accounts,
    operationData,
    serviceData,
    waiterOperationPointer.id,
    setIsWaiterHasPermissions,
  ]);

  return null;
};
